<template>
    <div v-if="!loading && invoices.length > 0">
        <hr class="my-6" />

        <div class="lg:grid lg:grid-cols-[3fr_7fr] lg:gap-6">
            <div>
                <h2 class="font-medium text-iron-700 tracking-[-0.01em]">
                    {{ $t("billing.invoices.title") }}
                </h2>
                <p
                    class="text-sm text-iron-500 mt-[3px]"
                    v-html="$t('billing.invoices.description')"
                ></p>
            </div>
            <div
                class="mb-4 mt-4 rounded-lg border-iron-200 bg-white lg:mt-0 flex w-full border"
            >
                <Table class="w-full" v-if="invoices.length > 0">
                    <TableHeader>
                        <TableRow class="text-xs text-iron-500">
                            <TableHead>
                                {{ $t("billing.invoices.invoice") }}
                            </TableHead>

                            <TableHead class="flex items-center justify-center">
                                {{ $t("billing.invoices.status") }}
                            </TableHead>
                            <TableHead>{{
                                $t("billing.invoices.amount")
                            }}</TableHead>
                            <TableHead> </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <TableRow
                            :class="[i % 2 === 0 ? 'bg-iron-25' : '']"
                            v-for="(invoice, i) in invoices"
                            :key="invoice.id"
                        >
                            <TableCell class="py-2">
                                <div class="gap-x-4 flex w-[180px]">
                                    <svg
                                        width="32"
                                        height="32"
                                        viewBox="0 0 32 32"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M3.82495 4C3.82495 2.13604 5.33599 0.625 7.19995 0.625H19.2C19.2903 0.625 19.377 0.660894 19.4409 0.724784L28.0752 9.3591C28.1391 9.42299 28.175 9.50965 28.175 9.6V28C28.175 29.864 26.6639 31.375 24.8 31.375H7.19995C5.33599 31.375 3.82495 29.864 3.82495 28V4Z"
                                            fill="white"
                                            stroke="#D1D1D6"
                                            stroke-width="1.25"
                                        />
                                        <path
                                            d="M19.2 0.399902V5.5999C19.2 7.80904 20.9908 9.5999 23.2 9.5999H28.4"
                                            stroke="#D1D1D6"
                                            stroke-width="1.25"
                                        />
                                        <path
                                            d="M20.5671 20.2752C19.6688 20.2752 18.5447 20.4319 18.1769 20.487C16.6547 18.8975 16.2217 17.9939 16.1243 17.753C16.2563 17.4138 16.7156 16.1253 16.7809 14.4704C16.813 13.6419 16.638 13.0228 16.2605 12.6303C15.8836 12.2385 15.4275 12.208 15.2967 12.208C14.838 12.208 14.0686 12.4399 14.0686 13.9931C14.0686 15.3407 14.6969 16.7707 14.8706 17.1401C13.9555 19.8045 12.9731 21.6284 12.7645 22.0035C9.08758 23.3879 8.80005 24.7265 8.80005 25.1059C8.80005 25.7876 9.28556 26.1946 10.0988 26.1946C12.0746 26.1946 13.8777 22.8774 14.1758 22.3014C15.5791 21.7422 17.4573 21.3959 17.9348 21.3133C19.3044 22.6179 20.8883 22.966 21.5461 22.966C22.041 22.966 23.2 22.966 23.2 21.7744C23.2 20.6678 21.7817 20.2752 20.5671 20.2752ZM20.4719 21.0575C21.5391 21.0575 21.8212 21.4104 21.8212 21.597C21.8212 21.7141 21.7767 22.0962 21.2047 22.0962C20.6917 22.0962 19.8061 21.7998 18.9347 21.1565C19.2981 21.1087 19.8358 21.0575 20.4719 21.0575ZM15.2407 12.9663C15.338 12.9663 15.402 12.9975 15.4548 13.0708C15.7618 13.4967 15.5143 14.8884 15.2131 15.9775C14.9223 15.0436 14.704 13.6108 15.0111 13.1067C15.0711 13.0083 15.1397 12.9663 15.2407 12.9663ZM14.7224 21.3022C15.1087 20.5216 15.5418 19.384 15.7777 18.7406C16.2497 19.5307 16.8846 20.2643 17.2518 20.662C16.1085 20.903 15.2435 21.1438 14.7224 21.3022ZM9.56757 25.21C9.54211 25.1798 9.53835 25.1162 9.55754 25.0398C9.59776 24.8796 9.90518 24.0859 12.1287 23.0913C11.8103 23.5928 11.3126 24.3093 10.7658 24.8447C10.3809 25.2049 10.0812 25.3876 9.87499 25.3876C9.80122 25.3876 9.69958 25.3675 9.56757 25.21Z"
                                            fill="#D92D20"
                                        />
                                    </svg>

                                    <div>
                                        <p
                                            class="text-sm font-medium text-iron-700"
                                        >
                                            {{ $t("billing.invoices.invoice") }}
                                            {{ invoice.internal_id }}
                                        </p>
                                        <p class="text-xs text-iron-500">
                                            {{
                                                format(
                                                    new Date(invoice.issued_at),
                                                    "PPP",
                                                )
                                            }}
                                        </p>
                                    </div>
                                </div>
                            </TableCell>

                            <TableCell class="flex justify-center">
                                <Badge variant="success">{{
                                    $t("billing.invoices.paid")
                                }}</Badge>
                            </TableCell>
                            <TableCell class="text-sm text-iron-700">
                                {{ invoice.amount_currency }}
                            </TableCell>
                            <TableCell class="text-right"
                                ><a
                                    :href="getDownloadRoute(invoice)"
                                    target="_blank"
                                    class="text-iron-500 hover:text-iron-700"
                                    ><CloudDownload></CloudDownload></a
                            ></TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { onMounted, ref } from "vue";
import route from "ziggy-js";
import axios from "axios";
import { type Team } from "../../Teams/types/Team";
import { useLocalisedTzFormat } from "@/composables/useLocalisedTzFormat";
import { Badge } from "@/components/ui/badge";
import { CloudDownload } from "lucide-vue-next";

interface Props {
    isTeamBilling: boolean;
    team?: Team;
}
const props = defineProps<Props>();

const { format } = useLocalisedTzFormat();

type Invoice = {
    id: number;
    issued_at: string;
    internal_id: string;
    amount_currency: string;
    type: string;
    status: string;
};

const invoices = ref<Invoice[]>([]);
const loading = ref(true);

function getDownloadRoute(invoice: Invoice) {
    if (props.isTeamBilling) {
        return route("app.teams.invoices.downloads.pdf", {
            invoice: invoice.id,
            team: props.team?.id,
        });
    }

    return route("app.invoices.downloads.pdf", {
        invoice: invoice.id,
    });
}

async function getPersonalInvoices() {
    try {
        const response = await axios.get(route("app.invoices.index"));
        invoices.value = response.data.data.invoices;
    } catch (error) {
        console.error(error);
    } finally {
        loading.value = false;
    }
}

async function getTeamInvoices() {
    try {
        const response = await axios.get(
            route("app.teams.invoices.index", { team: props.team?.id }),
        );
        invoices.value = response.data.data.invoices;
    } catch (error) {
        console.error(error);
    } finally {
        loading.value = false;
    }
}

function getInvoices() {
    if (props.team) {
        getTeamInvoices();
    } else {
        getPersonalInvoices();
    }
}

onMounted(() => {
    getInvoices();
});
</script>
